import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useAppState } from 'AppStateProvider';
import type { Contact, RevealBulkResponse } from 'domain/contact';
import { appStore } from 'services/appStore';
import { translate } from 'services/i18n';
import { useUseCases } from 'UseCaseProvider';

type UseRevealContactsFlowProps = {
  isExportAsLeadAfterReveal: boolean;
  onExportAsLeadAfterReveal: (revealResponse?: RevealBulkResponse, updatedContacts?: Contact[]) => void;
};

export const useRevealContactsFlow = ({
  isExportAsLeadAfterReveal,
  onExportAsLeadAfterReveal,
}: UseRevealContactsFlowProps) => {
  const { selectedContacts } = useAppState();
  const [revealResult, setRevealResult] = useState<RevealBulkResponse | undefined>(undefined);

  const contactsToReveal = selectedContacts.filter((c) => !c.isRevealed);
  const { revealContactsBulk } = useUseCases();

  const enrichContactsWithPrivateData = (response?: RevealBulkResponse) => {
    return selectedContacts.map((contact) => {
      const privateData = response?.contacts?.find((d) => d.id === contact.id);
      return privateData ? { ...contact, privateData, isRevealed: true } : contact;
    });
  };

  const proceedToExport = (response?: RevealBulkResponse) => {
    const enrichedContacts = enrichContactsWithPrivateData(response);

    appStore.setSelectedContacts(enrichedContacts.filter((c) => c.isRevealed));

    if (isExportAsLeadAfterReveal) {
      onExportAsLeadAfterReveal(response, enrichedContacts);
    } else {
      setRevealResult(response);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () => revealContactsBulk({ contactsToReveal, withFollowingExport: true }),
    onSuccess: proceedToExport,
    onError: () =>
      appStore.setErrorToast({
        content: translate('contacts.bulk_reveal.failed'),
        showCloseIcon: true,
      }),
  });

  return {
    onContinueToReveal: mutate,
    isPending,
    revealResult,
    resetRevealResult: () => setRevealResult(undefined),
  };
};
